import { CoreApiClient } from '../../core/client'
import { withQuery } from '../../core/query'

export const FULFILLMENT_TYPES = [
  'EXPRESS',
  'PICKUP',
  'INSTANT_DELIVERY',
  'AUTO_DELIVER',
  'CARD_DELIVER',
  'QR_VERIFY',
  'MANUAL_SERVICE',
  'SEAT_ADMISSION'
] as const

export type FulfillmentType = typeof FULFILLMENT_TYPES[number]
export type FulfillmentProfileStatus = 'ENABLED' | 'DISABLED'
export type FulfillmentStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'PARTIAL'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'

export interface FulfillmentProfile {
  id: number
  tenantId: number
  moduleCode: string
  fulfillmentType: FulfillmentType
  name: string
  config: string
  status: FulfillmentProfileStatus
  createdAt: string
  updatedAt: string
}

export interface SaveFulfillmentProfileRequest {
  moduleCode: string
  fulfillmentType: FulfillmentType
  name: string
  config: string
}

export interface FulfillmentStatusView {
  tenantId: number
  moduleCode: string
  orderId: number
  orderItemId: number | null
  fulfillmentType: FulfillmentType
  profileId: number | null
  status: FulfillmentStatus
  detail: string
  updatedAt: string
}

export interface ProductFulfillmentOption {
  id: number
  tenantId: number
  spuId: number
  skuId: number | null
  fulfillmentType: FulfillmentType
  profileId: number | null
  isDefault: boolean
  sortNo: number
}

export interface SaveProductFulfillmentOptionRequest {
  fulfillmentType: FulfillmentType
  profileId: number | null
  isDefault: boolean
  sortNo: number
}

export class FulfillmentApiModule {
  private readonly client: CoreApiClient

  constructor(client: CoreApiClient) {
    this.client = client
  }

  getSaasFulfillmentProfiles() {
    return this.client.get<FulfillmentProfile[]>('/api/saas/fulfillment/profiles')
  }

  saveSaasFulfillmentProfile(body: SaveFulfillmentProfileRequest) {
    return this.client.post<FulfillmentProfile>('/api/saas/fulfillment/profiles', body)
  }

  disableSaasFulfillmentProfile(profileId: number) {
    return this.client.post<FulfillmentProfile>(`/api/saas/fulfillment/profiles/${profileId}/disable`)
  }

  getSaasFulfillmentTypes(moduleCode: string) {
    return this.client.get<FulfillmentType[]>(withQuery('/api/saas/fulfillment/types', { moduleCode }))
  }

  getSaasFulfillmentStatus(orderId: number) {
    return this.client.get<FulfillmentStatusView[]>(withQuery('/api/saas/fulfillment/status', { orderId }))
  }

  getClientFulfillmentStatus(orderId: number) {
    return this.client.get<FulfillmentStatusView[]>(withQuery('/api/client/fulfillment/status', { orderId }))
  }

  getSaasProductFulfillmentOptions(spuId: number, skuId?: number | null) {
    return this.client.get<ProductFulfillmentOption[]>(withQuery(`/api/saas/fulfillment/products/${spuId}/options`, { skuId }))
  }

  saveSaasProductFulfillmentOptions(
    spuId: number,
    options: SaveProductFulfillmentOptionRequest[],
    skuId?: number | null
  ) {
    return this.client.post<void>(withQuery(`/api/saas/fulfillment/products/${spuId}/options`, { skuId }), options)
  }
}
